import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { siteInfo } from '@/mocks/site';

function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug],
  );

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.category === activeSlug),
    [activeSlug],
  );

  const otherCategoryVideos = useMemo(
    () => videos.filter((v) => v.category !== activeSlug).slice(0, 6),
    [activeSlug],
  );

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 国产在线一区高清${currentCategory.name}在线观看`
    : '视频分类';

  const pageDescription = currentCategory
    ? currentCategory.description
    : siteInfo.description;

  const pageKeywords = currentCategory
    ? `国产${currentCategory.name}在线观看,国产高清${currentCategory.name},${currentCategory.name}在线一区,国产${currentCategory.name}推荐`
    : '';

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`${siteInfo.url}/category/${activeSlug}`}
        ogType="website"
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          {/* Category Header */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                {currentCategory?.name || '视频分类'}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-6 py-4 border-b border-background-200 bg-background-50 sticky top-16 z-40">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Category Videos Grid */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              {categoryVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                  {categoryVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.slug}`}
                      className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="relative w-full aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          title={`${video.title} - 国产在线一区`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                        <div className="absolute top-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                          {video.quality}
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                          {video.duration}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-play-fill text-background-50 text-xl"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-2">
                          {video.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                          {video.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-foreground-400">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-eye-line text-xs"></i>
                            </div>
                            <span className="text-xs">{formatViews(video.views)}</span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-star-fill text-accent-500 text-xs"></i>
                            </div>
                            <span className="text-xs text-accent-600 font-medium">{video.rating}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-inbox-line text-2xl text-secondary-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无内容，请浏览其他分类</p>
                </div>
              )}
            </div>
          </section>

          {/* Other Categories Preview */}
          {otherCategoryVideos.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-12 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  其他分类推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                  {otherCategoryVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="relative w-full aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          title={`${video.title} - 国产在线一区`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                          {video.duration}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                          {video.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                          {video.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}